import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../css/OrderPlacement.css';
import { API_BASE } from '../config';

// --- SAFE IMAGE CLEANING HELPER ---
const getCleanImage = (imgData) => {
  if (!imgData) return 'https://placehold.co/150x150?text=2look+B2B';
  if (Array.isArray(imgData)) return imgData[0] || 'https://placehold.co/150x150?text=2look+B2B';
  try {
    const parsed = JSON.parse(imgData);
    if (Array.isArray(parsed)) return parsed[0] || 'https://placehold.co/150x150?text=2look+B2B';
  } catch (e) {}
  
  if (typeof imgData === 'string' && imgData.includes(',')) {
    return imgData.split(',')[0].trim();
  }
  return String(imgData).replace(/[\[\]"]/g, "").trim() || 'https://placehold.co/150x150?text=2look+B2B';
};

export default function OrderPlacementPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const checkoutProduct = location.state?.product;
  const initialQty = location.state?.quantity || 1;

  const [quantity, setQuantity] = useState(initialQty);
  const [partnerCode, setPartnerCode] = useState('');
  const [appliedPartner, setAppliedPartner] = useState(null); 
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isVerifying, setIsVerifying] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [renewalSuccess, setRenewalSuccess] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    gstNumber: '',
    address: '',
    city: '',
    pincode: '',
    paymentMethod: 'cod'
  });

  // --- AUTOMATIC RAZORPAY SCRIPT LOADER (FIX FOR CONSTRUCTOR ERROR) ---
  useEffect(() => {
    if (!checkoutProduct) {
      navigate('/');
      return;
    }

    // Dynamic script injection to prevent window.Razorpay is not a constructor error
    const scriptId = 'razorpay-checkout-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, [checkoutProduct, navigate]);

  const basePrice = checkoutProduct?.Price || checkoutProduct?.price || 0;
  const productTitle = checkoutProduct?.Title || checkoutProduct?.title || 'Unknown Product';
  const productId = checkoutProduct?.id || checkoutProduct?.Product_ID;
  const productMOQ = checkoutProduct?.MOQ || checkoutProduct?.moq || 1;
  const productStock = checkoutProduct?.Stock || checkoutProduct?.stock || 9999;
  
  const rawSubtotal = quantity * basePrice;
  
  useEffect(() => {
    if (appliedPartner && !membershipStatus?.isExpired) {
      const discount = (rawSubtotal * appliedPartner.discountPercent) / 100;
      setDiscountAmount(discount);
    } else {
      setDiscountAmount(0);
    }
  }, [rawSubtotal, appliedPartner, membershipStatus]);

  if (!checkoutProduct) return null;

  const subtotalAfterDiscount = rawSubtotal - discountAmount;
  const estimatedGST = Math.round(subtotalAfterDiscount * 0.18);
  const liveFinalPayableAmount = subtotalAfterDiscount + estimatedGST;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyPartnerCode = async () => {
    if (!partnerCode.trim()) return;
    setIsVerifying(true);

    try {
      const response = await fetch(`${API_BASE}/api/verify-partner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: partnerCode.trim() })
      });
      const data = await response.json();

      if (data.success) {
        const resolvedId = data.partnerId || data.id;

        if (data.isExpired) {
          setMembershipStatus({ 
            isExpired: true, 
            expiryDate: data.expiryDate,
            partnerId: resolvedId, 
            partnerName: data.partnerName 
          });
          setAppliedPartner(null);
        } else {
          setMembershipStatus({ isExpired: false });
          setAppliedPartner({
            id: resolvedId,
            name: data.partnerName,
            type: data.type,
            discountPercent: data.discountPercent
          });
        }
      } else {
        alert(data.message);
        setAppliedPartner(null);
      }
    } catch (err) {
      console.error(err);
      alert("Verification server connectivity issue!");
    } finally {
      setIsVerifying(false);
    }
  };

  // Razorpay Gateway Function for Both Orders and Renewals
  const openRazorpayGateway = async (amountInRupees, orderPayload, isRenewal = false) => {
    // Safety check for Razorpay SDK constructor availability
    if (typeof window.Razorpay === 'undefined') {
      alert("Payment gateway is still loading. Please try again in a few seconds.");
      return;
    }

    try {
      const orderRes = await fetch(`${API_BASE}/api/create-razorpay-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amountInRupees })
      });
      const orderData = await orderRes.json();

      if (!orderData.success) {
        alert("Failed to create Razorpay order.");
        return;
      }

      const options = {
        key: "rzp_test_TGTuUtY0PiWmUL", 
        amount: orderData.amount,
        currency: "INR",
        name: "2Look B2B",
        description: isRenewal ? "Annual Membership Renewal (1 Year)" : "Product Purchase Payment",
        order_id: orderData.razorpayOrderId,
        handler: async function (response) {
          console.log("Payment Successful Callback Triggered!", response);
          try {
            const verifyRes = await fetch(`${API_BASE}/api/verify-razorpay-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                orderDetails: orderPayload
              })
            });
            const verifyData = await verifyRes.json();
            console.log("Verification Data Result:", verifyData);

            if (verifyData.success) {
              if (isRenewal) {
                console.log("Calling Membership Renewal API for Partner ID:", orderPayload.partnerId);
                
                const renewApiRes = await fetch(`${API_BASE}/api/renew-membership`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ partnerId: orderPayload.partnerId })
                });
                const renewApiData = await renewApiRes.json();
                console.log("Renewal API Response:", renewApiData);

                if (renewApiData.success) {
                  setRenewalSuccess(true);
                  setTimeout(() => {
                    setRenewalSuccess(false);
                    setMembershipStatus(null);
                    setPartnerCode('');
                  }, 4000);
                } else {
                  alert("Payment successful, but membership extension failed: " + (renewApiData.message || "Unknown error"));
                }
              } else {
                await fetch(`${API_BASE}/api/orders`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(orderPayload)
                });
                setOrderPlaced(true);
                setTimeout(() => { navigate('/'); }, 3000);
              }
            } else {
              alert("Payment verification failed: " + verifyData.message);
            }
          } catch (err) {
            console.error("Verification/Renewal fetch error:", err);
            alert("Server error during payment verification or renewal process.");
          }
        },
        prefill: {
          name: orderPayload.buyerDetails.fullName,
          email: orderPayload.buyerDetails.email,
          contact: orderPayload.buyerDetails.phone
        },
        theme: {
          color: "#2563eb"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      console.error("Razorpay Error:", error);
      alert("Could not open payment gateway.");
    }
  };

  // Membership Renewal Handler (₹999)
  const handleStartRenewalPayment = () => {
    if (!membershipStatus) {
      alert("Error: Membership status object is missing!");
      return;
    }

    const partnerIdToRenew = membershipStatus.partnerId || membershipStatus.id;

    if (!partnerIdToRenew) {
      alert("Error: Partner ID could not be found!");
      return;
    }

    const renewalPayload = {
      partnerId: partnerIdToRenew,
      buyerDetails: {
        fullName: formData.fullName || membershipStatus.partnerName || 'Partner User',
        phone: formData.phone || '9999999999',
        email: formData.email || 'partner@renewal.com',
        companyName: membershipStatus.partnerName || 'Partner Business',
        gstNumber: formData.gstNumber || 'N/A',
        address: formData.address || 'Renewal Address',
        city: formData.city || 'N/A',
        pincode: formData.pincode || '000000',
        paymentMethod: 'netbanking'
      },
      orderedProduct: {
        id: 0,
        title: `1 Year Membership Renewal - ${membershipStatus.partnerName || 'Partner'}`,
        price: 999,
        quantity: 1
      },
      financialSummary: {
        subtotal: 999,
        discountApplied: 0,
        grandTotal: 999
      },
      membershipUsed: 'Renewal Fee'
    };

    openRazorpayGateway(999, renewalPayload, true);
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();

    const orderPayload = {
      partnerId: appliedPartner ? appliedPartner.id : null,
      buyerDetails: formData,
      orderedProduct: {
        id: productId,
        title: productTitle,
        price: basePrice,
        quantity: quantity
      },
      financialSummary: {
        subtotal: rawSubtotal,
        discountApplied: discountAmount,
        grandTotal: liveFinalPayableAmount
      },
      membershipUsed: appliedPartner ? appliedPartner.name : 'None'
    };

    if (formData.paymentMethod === 'netbanking') {
      openRazorpayGateway(liveFinalPayableAmount, orderPayload, false);
    } else {
      try {
        const response = await fetch(`${API_BASE}/api/orders`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orderPayload)
        });
        const data = await response.json();

        if (data.success) {
          setOrderPlaced(true);
          setTimeout(() => { navigate('/'); }, 3000);
        } else {
          alert(data.message || "Something went wrong while saving order.");
        }
      } catch (error) {
        console.error(error);
        alert("Server error! Order could not be pushed.");
      }
    }
  };

  if (orderPlaced) {
    return (
      <div className="order-success-screen">
        <span className="success-icon">🚀</span>
        <h2>Order Confirmed Successfully!</h2>
        <p>Thank you! Your payment has been verified and order registered.</p>
      </div>
    );
  }

  const cleanImgUrl = getCleanImage(checkoutProduct.images || checkoutProduct.Images);

  return (
    <div className="order-page-wrapper">
      <div className="order-split-layout">
        
        <div className="form-card-container">
          <h2 className="section-title">🏢 Shipping & Corporate Billing Details</h2>
          <form onSubmit={handleSubmitOrder} className="order-form">
            <div className="form-row-two-col">
              <div className="form-group">
                <label>Full Name *</label>
                <input required type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Rajesh Kumar" className="form-input" />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input required type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="10-digit number" className="form-input" />
              </div>
            </div>

            <div className="form-group">
              <label>Email Address</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="ops@company.com" className="form-input" />
            </div>

            <div className="form-row-two-col">
              <div className="form-group">
                <label>Company Name</label>
                <input type="text" name="companyName" value={formData.companyName} onChange={handleInputChange} placeholder="Enterprise Pvt Ltd" className="form-input" />
              </div>
              <div className="form-group">
                <label>GSTIN Number</label>
                <input type="text" name="gstNumber" value={formData.gstNumber} onChange={handleInputChange} placeholder="07AAAAA0000A1Z5" className="form-input" />
              </div>
            </div>

            <div className="form-group">
              <label>Detailed Delivery Address *</label>
              <textarea required name="address" value={formData.address} onChange={handleInputChange} placeholder="Full address..." rows="3" className="form-textarea" />
            </div>

            <div className="form-row-two-col">
              <div className="form-group">
                <label>City *</label>
                <input required type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder="New Delhi" className="form-input" />
              </div>
              <div className="form-group">
                <label>Pincode *</label>
                <input required type="text" name="pincode" value={formData.pincode} onChange={handleInputChange} placeholder="110001" className="form-input" />
              </div>
            </div>

            <div className="payment-method-section">
              <label className="payment-title">Select Mode of Settlement</label>
              <div className="radio-options-row">
                <label className="radio-label">
                  <input type="radio" name="paymentMethod" value="cod" checked={formData.paymentMethod === 'cod'} onChange={handleInputChange} />
                  Cash on Delivery
                </label>
                <label className="radio-label">
                  <input type="radio" name="paymentMethod" value="netbanking" checked={formData.paymentMethod === 'netbanking'} onChange={handleInputChange} />
                  B2B Corporate NetBanking / UPI (Razorpay)
                </label>
              </div>
            </div>

            <button type="submit" className="btn-confirm-order">Confirm & Place Order 📦</button>
          </form>
        </div>

        <div className="checkout-summary-column">
          <div className="summary-info-card">
            <h3 className="card-sub-title">Active Order Parameters</h3>
            <div className="product-summary-row">
              <img 
                src={cleanImgUrl} 
                alt={productTitle} 
                className="summary-thumb" 
                onError={(e) => { 
                  e.target.src = 'https://placehold.co/150x150?text=2look+B2B'; 
                }} 
              />
              <div>
                <h4 className="summary-product-title">{productTitle}</h4>
                <p className="summary-product-price">Unit Price: ₹{basePrice.toLocaleString('en-IN')}</p>
              </div>
            </div>
            <div className="qty-controller-row">
              <span className="qty-label">Order Volume Qty:</span>
              <div className="qty-stepper-box">
                <button type="button" onClick={() => setQuantity(prev => Math.max(productMOQ, prev - 1))}>-</button>
                <span className="qty-value-text">{quantity}</span>
                <button type="button" onClick={() => setQuantity(prev => Math.min(productStock, prev + 1))}>+</button>
              </div>
            </div>
          </div>

          <div className="voucher-apply-card">
            <h3>🏷️ Partner Membership Validation</h3>
            <div className="input-apply-group">
              <input type="text" value={partnerCode} onChange={(e) => setPartnerCode(e.target.value)} placeholder="E.g., ROYALCLUB10" className="voucher-input" />
              <button type="button" onClick={handleApplyPartnerCode} disabled={isVerifying} className="btn-apply-voucher">
                {isVerifying ? "..." : "Verify"}
              </button>
            </div>
            
            {membershipStatus?.isExpired && (
              <div style={{ marginTop: '10px', padding: '15px', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px' }}>
                <p style={{ color: '#991b1b', fontSize: '13px', margin: '0 0 8px 0' }}>⚠️ Membership expired on {new Date(membershipStatus.expiryDate).toLocaleDateString()}</p>
                <button 
                  type="button" 
                  onClick={handleStartRenewalPayment} 
                  style={{ width: '100%', padding: '10px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  Pay ₹999 & Renew Now 🔄
                </button>
              </div>
            )}

            {renewalSuccess && (
              <div style={{ marginTop: '10px', padding: '12px', background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '6px', color: '#166534', fontSize: '13px', fontWeight: 'bold', textAlign: 'center' }}>
                🎉 Membership successfully renewed for 1 full year!
              </div>
            )}

            {appliedPartner && (
              <div className="verified-success-badge">
                <span>⭐ <strong>{appliedPartner.name}</strong> ({appliedPartner.discountPercent}% Off)</span>
                <span onClick={() => {setAppliedPartner(null); setMembershipStatus(null);}} className="clear-badge-btn">✕</span>
              </div>
            )}
          </div>

          <div className="pricing-ledger-card">
            <h3>B2B Price Audit</h3>
            <div className="ledger-rows-stack">
              <div className="ledger-row">
                <span>Subtotal ({quantity} units × ₹{basePrice.toLocaleString('en-IN')})</span>
                <strong>₹{rawSubtotal.toLocaleString('en-IN')}.00</strong>
              </div>
              {discountAmount > 0 && (
                <div className="ledger-row discount-green">
                  <span>Membership Off</span>
                  <strong>- ₹{discountAmount.toLocaleString('en-IN')}.00</strong>
                </div>
              )}
              <div className="ledger-row">
                <span>GST Element (18%)</span>
                <strong>₹{estimatedGST.toLocaleString('en-IN')}.00</strong>
              </div>
              <hr className="dashed-divider" />
              <div className="ledger-row final-payable-row">
                <span>Net Payable Balance:</span>
                <span className="final-price">₹{liveFinalPayableAmount.toLocaleString('en-IN')}.00</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}